import React, { useState, useEffect, useMemo } from "react";

import Localization from "../../Localization";
import { formatAs } from "../../settings.js";

import "./styles.scss";

export default function Years(props) {
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [smallWidth, setSmallWidth] = useState(false);
  const [hoveredYear, setHoveredYear] = useState(null);

  const [activeProps, setActiveProps] = useState(props);
  useEffect(() => {
    const delay = props.delay ? 600 : 0;
    const delayHandle = setTimeout(() => setActiveProps(props), delay);
    return () => clearTimeout(delayHandle);
  }, [props]);

  const {
    years,
    valueKey,
    width,
    allYears,
    selectedYears,
    updateYears,
    relevantTooltip,
    lockedProject,
    updateLockedProject,
    language,
    setFocusedYear,
    focusedYear,
  } = activeProps;

  useEffect(() => {
    setSelected(selectedYears);
  }, [selectedYears]);

  useEffect(() => {
    const isSmallWidth = width <= 1024;
    setSmallWidth(isSmallWidth);
  }, [width]);

  const {
    sideWidth,
    leftSideWidth,
    centerWidth,
    yearWidth,
    barWidth,
    barMarginLeft,
    selectedWidth,
    fontSize,
  } = useMemo(() => {
    const minFontSize = 6;
    const sideWidth = smallWidth ? Math.max(75, width * 0.18) : width * 0.15;
    const leftSideWidth = smallWidth ? minFontSize : sideWidth;
    const centerWidth = width - (sideWidth + leftSideWidth);
    const yearWidth = Math.floor(centerWidth / (allYears.length || 1));
    const selectedWidth = yearWidth * years.length;
    const barWidth = Math.floor(yearWidth * 0.8) + 1;
    const barMarginLeft = Math.floor(yearWidth * 0.1);
    const fontSize = allYears.length
      ? Math.max(minFontSize, yearWidth / 3)
      : minFontSize;
    return {
      sideWidth,
      leftSideWidth,
      centerWidth,
      yearWidth,
      barWidth,
      barMarginLeft,
      selectedWidth,
      fontSize,
    };
  }, [width, smallWidth, allYears.length, years.length]);

  const allYearsSelected = allYears.length === selectedYears.length;

  const toggleYear = useMemo(
    () =>
      function (y) {
        if (selected.includes(y)) {
          setSelected(selected.filter((s) => s !== y));
        } else {
          setSelected(selected.concat([y]));
        }
      },
    [selected]
  );

  const yearBars = useMemo(() => {
    let projectLabel = null;
    return (
      <div
        className="yearBars"
        style={{
          width: selectedWidth,
          left: (centerWidth - selectedWidth) / 2,
        }}
      >
        {years.map((y, i) => {
          return (
            <div
              key={y.name}
              className={`year ${focusedYear === y.name ? "focused" : ""}`}
              style={{
                width: yearWidth,
                left: i * yearWidth,
                opacity: selectedYears.includes(y.name) ? 1 : 0.3,
              }}
              onClick={() => {
                updateYears([y.name]);
                setFocusedYear(y.name);
              }}
              onMouseEnter={() => setHoveredYear(y.name)}
              onMouseLeave={() => setHoveredYear(null)}
            >
              <div
                key={`${y.name}-bars`}
                className="bars"
                style={{
                  marginTop: -Math.floor(y.height),
                  height: Math.floor(y.height) + 1,
                  overflow: lockedProject ? "visible" : "hidden",
                }}
              >
                {y.groups.map((g) =>
                  g.projects.map((p) => {
                    const backgroundColor = g.fill;
                    let opacity = 1;
                    let locked = false;
                    if (relevantTooltip || lockedProject) {
                      if (lockedProject) {
                        locked = lockedProject.plantID === p.plantID;
                      }
                      const [project] = lockedProject
                        ? [lockedProject]
                        : relevantTooltip.data.projects;
                      if (project.plantID !== p.plantID) {
                        opacity = 0.3;
                      } else {
                        const left = yearWidth + i * yearWidth;
                        const top =
                          -Math.floor(y.height) +
                          Math.floor(p.top + p.height / 2);
                        projectLabel = (
                          <div
                            className="barLabel"
                            style={{ top, left, backgroundColor }}
                            onClick={() =>
                              updateLockedProject(project, false, false)
                            }
                          >
                            <div>{project.plant}</div>{" "}
                            {locked ? <div className="close">x</div> : null}
                          </div>
                        );
                      }
                    }
                    return (
                      <div
                        id={p.plantID}
                        key={p.plantID}
                        className={`bar ${locked ? "locked" : ""}`}
                        style={{
                          width: barWidth,
                          marginLeft: barMarginLeft,
                          height: Math.floor(p.height) + 1,
                          backgroundColor,
                          opacity,
                        }}
                      />
                    );
                  })
                )}
              </div>
              <div
                className={`total ${hoveredYear === y.name ? "visible" : ""}`}
                style={{ marginTop: -Math.floor(y.height) }}
              >
                {formatAs.numberInt(Math.round(y.total))}
                <br />
                {valueKey === "co2emissions" ? Localization.get("K Ton") : "MW"}
              </div>
              <div className="label">{y.name}</div>
            </div>
          );
        })}
        {projectLabel}
      </div>
    );
  }, [
    centerWidth,
    focusedYear,
    hoveredYear,
    lockedProject,
    relevantTooltip,
    selectedWidth,
    selectedYears,
    updateLockedProject,
    updateYears,
    valueKey,
    yearWidth,
    barWidth,
    barMarginLeft,
    years,
    setFocusedYear,
  ]);

  const yearModal = useMemo(
    () => (
      <div className={`yearModal ${showModal ? "active" : ""}`}>
        <div className="title">
          <h2>{Localization.get("Timeframe", false, language)}</h2>
        </div>
        <div className="options">
          <div className="checkboxes">
            {allYears.map((y) => {
              return (
                <div key={y} className="toggle" onClick={() => toggleYear(y)}>
                  <input
                    type="checkbox"
                    id={`toggle-${y}`}
                    name={`toggle-${y}`}
                    checked={selected.includes(y)}
                    readOnly
                  />
                  <label htmlFor={`toggle-${y}`}>{y}</label>
                </div>
              );
            })}
          </div>
          <div className="groupToggles">
            <div className="button" onClick={() => setSelected(allYears)}>
              <button>{Localization.get("Select All")}</button>
            </div>
            <div className="button" onClick={() => setSelected([])}>
              <button>{Localization.get("Deselect All")}</button>
            </div>
          </div>
        </div>
        <div className="buttons">
          <div className="button" onClick={() => setShowModal(false)}>
            <button>{Localization.get("Cancel")}</button>
          </div>
          <div
            className="button"
            onClick={() => {
              updateYears(selected);
              setShowModal(false);
              // Clear focused year when applying multiple years
              if (selected.length !== 1) {
                setFocusedYear(null);
              } else {
                setFocusedYear(selected[0]);
              }
            }}
          >
            <button>{Localization.get("Apply")}</button>
          </div>
        </div>
      </div>
    ),
    [
      allYears,
      selected,
      showModal,
      toggleYear,
      updateYears,
      language,
      setFocusedYear,
    ]
  );

  return (
    <div className="Years" style={{ fontSize }}>
      <div
        className="side"
        // style={{ width: sideWidth, display: smallWidth ? 'none' : 'flex' }}
        style={{ width: leftSideWidth }}
      />
      <div className="center" style={{ width: centerWidth }}>
        {yearBars}
      </div>
      <div className="side" style={{ width: sideWidth }}>
        <div
          className="yearToggle"
          onClick={() => {
            if (allYearsSelected) {
              updateYears([]);
              setFocusedYear(null);
            } else {
              updateYears(allYears);
              setFocusedYear(null);
            }
          }}
        >
          <input
            type="checkbox"
            id="yearToggle"
            name="yearToggle"
            checked={allYearsSelected}
            readOnly
          />
          <label htmlFor="yearToggle">All</label>
        </div>
        <div
          className="button yearModalToggle"
          onClick={() => setShowModal(!showModal)}
        >
          <button>{Localization.get("Change Timeframe")}</button>
        </div>
      </div>
      {yearModal}
    </div>
  );
}
